/**
 * Utilitário para exibição de status de entrega de mensagens (email, WhatsApp, etc.).
 * Consome o objeto deliveryStatus retornado pela API (já parseado no backend).
 *
 * Uso: getDeliveryStatusDisplay(message) => { label, icon, errorReason, isPending, isError }
 */

/** Mapeamento status WhatsApp (numérico) para chave de ícone */
const WHATSAPP_STATUS_ICON = {
  0: "pending",   // Enviando
  1: "sent",      // Enviado
  2: "delivered", // Entregue
  3: "read",      // Lido
  4: "error",     // Falha
};

/** Normaliza labels do provedor/API para o padrão PT-BR exibido na UI. */
function normalizeDeliveryLabel(ds) {
  const raw = ds?.label;
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  const lower = s.toLowerCase();
  const map = {
    pending: "Enviando",
    sending: "Enviando",
    queued: "Enviando",
    sent: "Enviado",
    delivered: "Entregue",
    read: "Lido",
    opened: "Lido",
    clicked: "Lido e clicado",
    bounced: "E-mail inválido ou inexistente",
    dropped: "Erro na entrega",
    complaint: "Marcado como spam pelo destinatário",
    complained: "Marcado como spam pelo destinatário",
    failed: "Falha no envio",
    error: "Falha no envio",
    rejected: "Rejeitado",
    expired: "Expirado",
    undelivered: "Não entregue",
    undeliverable: "Não entregue",
    "message undeliverable": "Não entregue",
    blocked: "Bloqueado",
    spam: "Marcado como spam",
    invalid: "Número inválido",
  };

  const exact = map[lower];
  if (exact) return exact;

  if (lower.includes("undeliver")) return "Não entregue";
  if (lower.includes("fail") || lower.includes("error")) return "Falha no envio";
  if (lower.includes("reject") || lower.includes("block")) return "Rejeitado";
  if (lower.includes("expir")) return "Expirado";
  if (lower.includes("spam")) return "Marcado como spam";

  return s;
}

/**
 * Retorna dados para exibir o status de entrega na UI.
 * @param {object} message - mensagem com deliveryStatus (e opcionalmente updatedAt para “Enviando”)
 * @returns {{ label: string | null, icon: string | null, errorReason: string | null, isPending: boolean, isError: boolean }}
 */
export function getDeliveryStatusDisplay(message) {
  const ds = message?.deliveryStatus;
  if (!ds || (ds.label == null && ds.errorReason == null)) {
    return { label: null, icon: null, errorReason: null, isPending: false, isError: false };
  }

  const ERROR_STATUSES = new Set([
    "bounced", "dropped", "complaint", "complained", "failed", "error",
    "rejected", "expired", "undelivered", "undeliverable",
    "blocked", "spam", "invalid",
  ]);
  const statusStr = typeof ds.status === "string" ? ds.status.toLowerCase() : "";
  const isError = ds.status === 4 || ds.status === -1 || ERROR_STATUSES.has(statusStr) || !!ds.errorReason;
  const isPending = ds.status === 0 || ds.status === "sending" || ds.status === "queued";

  let icon = null;
  if (typeof ds.status === "number" && WHATSAPP_STATUS_ICON[ds.status]) {
    icon = WHATSAPP_STATUS_ICON[ds.status];
  } else if (ds.status === -1) {
    icon = "error";
  } else if (ds.status === "sent") {
    icon = "sent";
  } else if (ds.status === "opened" || ds.status === "clicked" || ds.status === "read") {
    icon = "read";
  } else if (ds.status === "delivered") {
    icon = "delivered";
  } else if (isError) {
    icon = "error";
  }

  return {
    label: normalizeDeliveryLabel(ds),
    icon,
    errorReason: ds.errorReason || null,
    isPending,
    isError,
  };
}

/**
 * Regras de mapeamento: cada entrada tem um teste (regex ou função sobre o texto lower-case)
 * e o texto amigável correspondente. A primeira que bater é retornada.
 */
const FRIENDLY_ERROR_RULES = [
  // ──── Meta IG/Messenger: janela de mensagens (24h) ────
  {
    test: (l) =>
      /\b1545041\b/.test(l) ||
      /meta_window_(closed|expired)/i.test(l) ||
      (/(instagram|messenger)/i.test(l) &&
        /(janela|window|24\s*h|24\s*horas)/i.test(l) &&
        /(fechada|closed|expir|outside|atendente humano|human.?agent)/i.test(l)),
    msg:
      "A janela de 24 horas do Instagram/Messenger expirou. " +
      "Aguarde o cliente enviar uma nova mensagem para reabrir o atendimento.",
  },

  // ──── Janela 24h / re-engagement ────
  {
    test: (l) =>
      (/\b(131047|131042)\b/.test(l) ||
        (/(re-?engagement|outside.*window|janela.*24|24.*hour.*window|fora da janela|session.*expir)/i.test(l) &&
          !/131049/.test(l) &&
          !/(instagram|messenger)/i.test(l))),
    msg:
      "A janela de 24 horas do WhatsApp expirou. " +
      "Para reabrir o contato, envie um template de reengajamento pelo campo de mensagem ou aguarde o cliente responder.",
  },

  // ──── Meta: Healthy ecosystem engagement (131049) ────
  {
    test: (l) =>
      /\b131049\b/.test(l) ||
      /healthy\s*ecosystem|ecosystem\s*engagement|maintain\s*healthy\s*ecosystem/i.test(l),
    msg:
      "O WhatsApp bloqueou este envio para manter o engajamento saudável (código 131049). " +
      "Isso costuma acontecer quando o destinatário não interage com mensagens recentes ou quando há tentativas repetidas de contato. " +
      "Tente uma destas opções: (1) aguarde o cliente responder para retomar a conversa; (2) use um template aprovado (reengajamento) quando aplicável; " +
      "(3) evite reenvios em sequência e revise a estratégia de conteúdo. Se acontecer com frequência, verifique no Gerenciador da Meta se o número está com restrições/qualidade baixa.",
  },

  // ──── Template: parâmetros incorretos (Meta) ────
  {
    test: (l) => l.includes("oauthexception") && l.includes("number of parameters"),
    msg:
      "O WhatsApp rejeitou a mensagem porque a quantidade de variáveis do template não confere. " +
      "Peça ao responsável pelo canal para revisar o template na Meta/XPRO e alinhar o número e a ordem das variáveis.",
  },

  // ──── Template: conteúdo não bate (BSP / genérico) ────
  {
    test: (l) =>
      l.includes("template did not match") ||
      (l.includes("template") && l.includes("did not match")),
    msg:
      "O conteúdo enviado não corresponde ao template aprovado pelo WhatsApp. " +
      "Verifique se selecionou o template correto e preencheu todos os campos obrigatórios.",
  },

  // ──── Template não encontrado / pausado / desativado ────
  {
    test: (l) =>
      /template.*(not\s*found|não\s*encontrad|does\s*not\s*exist|paused|disabled|desativad)/i.test(l),
    msg:
      "O template utilizado não foi encontrado ou está desativado/pausado. " +
      "Verifique no painel da Meta se o template existe, está ativo e aprovado.",
  },

  // ──── Número inválido para SMS (Twilio / provedor) ────
  {
    test: (l) =>
      /invalid\s*'?to'?\s*phone\s*number/i.test(l) ||
      /invalid\s*'?from'?\s*phone\s*number/i.test(l) ||
      /\b21211\b/.test(l) ||
      (/número do destinatário inválido/i.test(l) && /sms|ddd|país/i.test(l)),
    msg:
      "O número do destinatário é inválido para SMS. " +
      "Verifique se está completo com DDD e código do país (ex.: +55), e tente novamente.",
  },

  // ──── Twilio SMS: Geo Permissions (só com código/frase específicos — não genérico) ────
  {
    test: (l) =>
      /\b21408\b/.test(l) ||
      /permission to send an sms has not been enabled for the region/i.test(l),
    msg:
      "A conta Twilio não tem permissão para enviar SMS para a região deste número (ex.: Brasil). " +
      "No Console Twilio, em Messaging → Settings → Geo Permissions, habilite o país do destinatário e tente novamente.",
  },

  // ──── Twilio SMS: opt-out ────
  {
    test: (l) => /\b21610\b/.test(l) || /unsubscribed recipient/i.test(l),
    msg:
      "Este número pediu para não receber mais SMS (opt-out / STOP). " +
      "Só é possível enviar de novo se o contato responder START ou for removido da lista de bloqueio no Twilio.",
  },

  // ──── Twilio SMS: não é celular ────
  {
    test: (l) => /\b21614\b/.test(l) || /not a valid mobile number/i.test(l),
    msg:
      "O destino não é um celular válido para SMS (pode ser fixo ou formato incorreto). " +
      "Atualize o celular do contato com DDD e tente novamente.",
  },

  // ──── Twilio SMS: inacessível / operadora (30003) ────
  {
    test: (l) =>
      /\b30003\b/.test(l) ||
      /unreachable destination handset/i.test(l) ||
      /destination handset.*(unavailable|unreachable)/i.test(l) ||
      /destinat[aá]rio inacess/i.test(l) ||
      /celular do destinat[aá]rio inacess/i.test(l),
    msg:
      "Celular do destinatário inacessível no momento (desligado, sem sinal, fora da rede ou temporariamente indisponível). " +
      "Confirme se o aparelho está ligado e recebe SMS (teste com outro remetente) e tente reenviar em alguns minutos.",
  },
  {
    test: (l) => /\b30006\b/.test(l) || /landline or unreachable carrier/i.test(l),
    msg:
      "Destino é linha fixa ou a operadora não aceita SMS neste número. " +
      "Use um celular válido com DDD ou outro canal (WhatsApp).",
  },
  {
    test: (l) =>
      /\b30007\b/.test(l) ||
      /carrier violation|message filtered/i.test(l),
    msg:
      "Operadora filtrou a mensagem (conteúdo ou reputação do remetente). " +
      "Evite links/spam, revise o texto e tente novamente. Se persistir, fale com o suporte.",
  },
  {
    test: (l) =>
      /\b30004\b/.test(l) ||
      (/message blocked/i.test(l) && /twilio|sms/i.test(l)),
    msg:
      "Mensagem bloqueada pela operadora ou pela Twilio. " +
      "Revise o conteúdo e confirme se o número não está em lista de bloqueio.",
  },
  {
    test: (l) => /\b30005\b/.test(l) || /unknown destination handset/i.test(l),
    msg:
      "Destino desconhecido pela operadora. Verifique se o número está correto e ativo.",
  },
  {
    test: (l) => /\b30008\b/.test(l),
    msg:
      "Falha desconhecida na entrega do SMS. Aguarde e tente reenviar. " +
      "Se repetir, abra o Monitor → Errors no Console Twilio para ver o código exato da falha.",
  },

  // ──── Número inválido / não registrado no WhatsApp ────
  {
    test: (l) =>
      /(not.*valid.*whatsapp|not.*registered|incapable.*receiving)/i.test(l) ||
      (/(invalid.*number|número.*inválid|recipient.*not.*valid)/i.test(l) &&
        !/invalid\s*'?(?:to|from)'?\s*phone\s*number/i.test(l)) ||
      /\b(131026|131025)\b/.test(l),
    msg:
      "O número do destinatário é inválido ou não está registrado no WhatsApp. " +
      "Verifique se o número está correto, incluindo código de país (ex.: +55), e tente novamente.",
  },

  // ──── Contato bloqueou a empresa ────
  {
    test: (l) => /(block|bloqueou|opt.?out)/i.test(l) && !/(spam|complaint)/i.test(l),
    msg:
      "O contato bloqueou o recebimento de mensagens desta empresa. " +
      "Não é possível enviar mensagens até que o contato desbloqueie o número.",
  },

  // ──── Rate limit / throttle ────
  {
    test: (l) => /(rate.?limit|too many|too_many|throttl|limit.*exceeded|429)/i.test(l),
    msg:
      "O envio foi bloqueado por limite de taxa do provedor. " +
      "Aguarde alguns minutos e tente reenviar a mensagem.",
  },

  // ──── Limite de qualidade da Meta / spam / compliance ────
  {
    test: (l) =>
      /(quality|spam|complaint|compliance|policy|violat|abus)/i.test(l) ||
      /\b131048\b/.test(l),
    msg:
      "O WhatsApp restringiu o envio por questões de qualidade ou política de uso. " +
      "Revise o conteúdo da mensagem e verifique no Gerenciador da Meta se o número está com restrições.",
  },

  // ──── Mídia inválida ou não suportada ────
  {
    test: (l) =>
      /(media.*invalid|media.*not.*support|unsupported.*media|mídia.*inválid|mídia.*não.*suportad|file.*too.*large|arquivo.*grande|invalid.*media|media.*size)/i.test(l) ||
      /\b131052\b/.test(l),
    msg:
      "A mídia enviada é inválida, muito grande ou tem formato não suportado pelo WhatsApp. " +
      "Reduza o tamanho do arquivo ou converta para um formato aceito (ex.: imagem JPG/PNG até 5 MB, vídeo MP4 até 16 MB).",
  },

  // ──── Parâmetro inválido / código 1009 (BSP–Meta) ────
  {
    test: (l) =>
      /\b1009\b/.test(l) ||
      /\(#\s*1009\s*\)/.test(l) ||
      (/\binvalid parameter\b/.test(l) && /null\s*,/.test(l)),
    msg:
      "Parâmetro inválido na montagem do envio (código 1009). Verifique texto, anexos e links; confira também se o recurso está habilitado no WhatsApp Business (WABA) e no painel do provedor quando aplicável.",
  },

  // ──── Erro de autenticação do canal ────
  {
    test: (l) =>
      /(auth.*fail|unauthorized|token.*expir|token.*invalid|credential|access.*denied|forbidden|401|403)/i.test(l) &&
      !/(block|opt.?out)/i.test(l),
    msg:
      "Houve um erro de autenticação com o provedor do canal. " +
      "Peça ao administrador para verificar as credenciais/token do canal nas configurações.",
  },

  // ──── Número do remetente não configurado / desconectado ────
  {
    test: (l) =>
      /(sender.*not.*found|remetente.*não|from.*number.*not|channel.*disconnect|canal.*desconect|phone.*not.*register|número.*não.*configur)/i.test(l),
    msg:
      "O número remetente não foi encontrado ou o canal está desconectado. " +
      "Verifique a conexão do canal nas configurações e reconecte se necessário.",
  },

  // ──── Timeout / provedor indisponível ────
  {
    test: (l) => /(timeout|timed?\s*out|indisponível|unavailable|503|502|504|connection.*refused)/i.test(l),
    msg:
      "O provedor do canal está temporariamente indisponível. " +
      "Aguarde alguns instantes e tente reenviar a mensagem.",
  },

  // ──── Mensagem muito longa ────
  {
    test: (l) => /(too long|muito longa|character.*limit|body.*size|payload.*too.*large)/i.test(l),
    msg:
      "A mensagem excede o limite de caracteres permitido pelo WhatsApp. " +
      "Reduza o texto e tente novamente.",
  },

  // ──── E-mail: bounced ────
  {
    test: (l) => /(bounce|devolvid|mailbox.*full|caixa.*cheia|user.*unknown|recipient.*reject)/i.test(l),
    msg:
      "O e-mail não pôde ser entregue. O endereço pode estar incorreto, a caixa cheia ou o servidor de destino rejeitou a mensagem. " +
      "Verifique o endereço de e-mail do contato e tente novamente.",
  },

  // ──── Genérico "undeliverable" / "message undeliverable" ────
  {
    test: (l) => /undeliver/i.test(l),
    msg:
      "A mensagem não pôde ser entregue ao destinatário. " +
      "Verifique se o número/endereço do contato está correto e se o canal está conectado.",
  },

  // ──── Genérico "failed" sem detalhes mais específicos ────
  {
    test: (l) => /\b(fail|falha|error|erro)\b/i.test(l),
    msg:
      "O envio da mensagem falhou. " +
      "Verifique a conexão do canal e tente reenviar. Se o problema persistir, entre em contato com o suporte.",
  },
];

/**
 * Modelo com mídia no cabeçalho enviado sem o arquivo.
 * Ex.: "null, (#2012) header: Format mismatch, expected IMAGE, received UNKNOWN"
 */
const TEMPLATE_HEADER_MEDIA_MISSING =
  /format mismatch,\s*expected\s+(image|video|document|audio),\s*received\s+unknown/i;

function friendlyTemplateHeaderMediaMissing(reason) {
  const match = String(reason || "").match(TEMPLATE_HEADER_MEDIA_MISSING);
  if (!match) return null;
  const kind = String(match[1] || "").toLowerCase();
  const byKind = {
    image:
      "Este modelo pede uma imagem e ela não foi anexada, por isso o WhatsApp não aceitou a mensagem. " +
      "Anexe a imagem (botão Anexar arquivo) e envie de novo. Tentar reenviar sem a imagem repete a mesma recusa.",
    document:
      "Este modelo pede um arquivo e nenhum foi anexado, por isso o WhatsApp não aceitou a mensagem. " +
      "Anexe o arquivo (botão Anexar arquivo) e envie de novo. Tentar reenviar sem o arquivo repete a mesma recusa.",
    video:
      "Este modelo pede um vídeo e nenhum foi anexado, por isso o WhatsApp não aceitou a mensagem. " +
      "Anexe o vídeo (botão Anexar arquivo) e envie de novo. Tentar reenviar sem o vídeo repete a mesma recusa.",
    audio:
      "Este modelo pede um áudio e nenhum foi anexado, por isso o WhatsApp não aceitou a mensagem. " +
      "Anexe o áudio (botão Anexar arquivo) e envie de novo. Tentar reenviar sem o áudio repete a mesma recusa.",
  };
  return (
    byKind[kind] ||
    "Este modelo pede uma imagem ou um arquivo e nada foi anexado, por isso o WhatsApp não aceitou a mensagem. " +
      "Anexe o que o modelo pede (botão Anexar arquivo) e envie de novo. Tentar reenviar sem o anexo repete a mesma recusa."
  );
}

/**
 * Converte mensagens técnicas de erro do provedor em textos mais amigáveis
 * para o atendente, explicando o que aconteceu e como resolver.
 */
export function getFriendlyDeliveryError(errorReason) {
  if (!errorReason) return null;
  const raw = String(errorReason).trim();
  if (!raw) return null;

  const missingHeaderMedia = friendlyTemplateHeaderMediaMissing(raw);
  if (missingHeaderMedia) return missingHeaderMedia;

  const lower = raw.toLowerCase();

  for (const rule of FRIENDLY_ERROR_RULES) {
    if (rule.test(lower)) return rule.msg;
  }

  return raw;
}

/**
 * Monta `deliveryStatus` para mensagens enviadas no WhatsApp pessoal (Redis: statusWhatsapp 0–4).
 * Mesma escala usada no canal comercial (`WHATSAPP_STATUS_ICON`).
 */
export function deliveryStatusFromPersonalWhatsappStatus(statusWhatsapp) {
  const sw = statusWhatsapp != null ? Number(statusWhatsapp) : 0;
  if (!Number.isFinite(sw) || sw < 0 || sw > 4) {
    return { status: 1, label: "Enviado" };
  }
  const labels = {
    0: "Enviando",
    1: "Enviado",
    2: "Entregue",
    3: "Lido",
    4: "Falha no envio",
  };
  return { status: sw, label: labels[sw] ?? "Enviado" };
}

/**
 * Retorna se a mensagem deve exibir linha de status (apenas mensagens enviadas pelo agente com canal que tem status).
 */
export function shouldShowDeliveryStatus(message, isUser) {
  if (message?.isChatbotSimulation) return false;
  if (!isUser) return false;
  try {
    const raw = message?.json_whatsapp;
    const j = raw == null ? null : typeof raw === "string" ? JSON.parse(raw) : raw;
    if (j && (j.xpro_transport === "twilio_voice" || j?.xpro_voice?.spoken)) return false;
  } catch {
    /* ignore */
  }
  const ds = message?.deliveryStatus;
  if (!ds) return false;
  return ds.label != null || ds.errorReason != null;
}

/** Erros que NÃO são de janela 24h (ex.: 131049 = ecosystem); não exibir mensagem de reengajamento. */
const NOT_REENGAGEMENT = [
  /131049|#131049/i,
  /engajamento\s*saudável|healthy\s*ecosystem|ecosystem\s*engagement/i,
];

/** Indica se o erro é de janela de 24h do WhatsApp (re-engagement / código 470 ou 131042). */
const REENGAGEMENT_PATTERNS = [
  /re-engagement/i,
  /#470\b/i,
  /\(\s*470\s*\)/i,
  /#131042\b/i,
  /(janela|window).*(24\s*h|24\s*hour).*(fechada|expirada|expirou|closed|expired)/i,
  /(fora da janela|outside.*window|24\s*hour.*window)/i,
];

/**
 * Verifica se o errorReason indica erro de reengajamento (janela de 24h fechada).
 * Erro 131049 (healthy ecosystem) não é reengajamento — exibir a mensagem real.
 * @param {string | null} errorReason
 * @returns {boolean}
 */
export function isReengagementError(errorReason) {
  if (errorReason == null || typeof errorReason !== "string") return false;
  const s = errorReason.trim();
  if (NOT_REENGAGEMENT.some((re) => re.test(s))) return false;
  return REENGAGEMENT_PATTERNS.some((re) => re.test(s));
}

/** Mensagem amigável para exibir quando o envio falhou por janela de 24h. */
export const REENGAGEMENT_FRIENDLY_MESSAGE =
  "Janela de 24h do WhatsApp fechada. Para responder, use um template de reengajamento no campo de mensagem ou aguarde o cliente enviar uma nova mensagem.";
